import {
  Bell,
  Check,
  CheckCheck,
  AlertTriangle,
  ClipboardCheck,
  Calendar,
  User,
  Users,
  MessageSquare,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router";
import { formatDistanceToNow } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useNotifications, NotificationType } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getLocalizedNotification } from "@/lib/notificationLocalization";

const notificationIcons: Record<NotificationType, typeof Bell> = {
  assignment: User,
  assignment_to_created: Users,
  due_reminder: Calendar,
  overdue: AlertTriangle,
  defect_created: AlertTriangle,
  defect_status_changed: Check,
  defect_comment: MessageSquare,
  defect_closed: ShieldCheck,
  inspection_completed: ClipboardCheck,
  escalation: AlertTriangle,
  // TES-2053: zu-verifizieren-Task (Mig 380/381) — Handlungsbedarf, daher warning statt success.
  inspection_to_verify: ClipboardCheck,
  // TES-2082: Owner-Deaktivierung → Re-Staffing (Mig 388/389) — Personen/Gruppen-Bezug (Nachbesetzung).
  owner_restaffing: Users,
};

const notificationColors: Record<NotificationType, string> = {
  assignment: "text-primary",
  assignment_to_created: "text-info",
  due_reminder: "text-warning",
  overdue: "text-destructive",
  defect_created: "text-destructive",
  defect_status_changed: "text-success",
  defect_comment: "text-info",
  defect_closed: "text-success",
  inspection_completed: "text-success",
  escalation: "text-destructive",
  inspection_to_verify: "text-warning",
  // TES-2082: Handlungsbedarf (qm_admin muss nachbesetzen) — warning wie inspection_to_verify.
  owner_restaffing: "text-warning",
};

// TES-2028: Icon/Farbe für einen (ggf. dem Client noch unbekannten) Typ auflösen — der Server
// erweitert notification_type zuerst (Mig 359), daher defensiver Bell/muted-Fallback statt Crash.
// Als eigenständiger Helper (nicht inline im map-Callback), damit die Fallback-Zweige die
// Komplexität der Render-Schleife nicht erhöhen.
function resolveNotificationVisual(type: NotificationType): {
  Icon: typeof Bell;
  colorClass: string;
} {
  return {
    Icon: notificationIcons[type] ?? Bell,
    colorClass: notificationColors[type] ?? "text-muted-foreground",
  };
}

// TES-1664: pre-existing notification menu complexity; this PR only labels the icon trigger.
// fallow-ignore-next-line complexity
export function NotificationBell() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const dateLocale = i18n.language === "de" ? de : enUS;

  // fallow-ignore-next-line complexity
  const handleNotificationClick = (notification: (typeof notifications)[0]) => {
    markAsRead(notification.id);

    // Navigate based on entity type
    if (notification.entity_type && notification.entity_id) {
      switch (notification.entity_type) {
        case "inspection":
          navigate(`/inspections/${notification.entity_id}`);
          break;
        case "defect":
          navigate(`/defects`);
          break;
        case "task":
          navigate(`/tasks`);
          break;
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative h-9 w-9"
          aria-label={t("notifications.title")}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>{t("notifications.title")}</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-1 text-xs"
              onClick={(e) => {
                e.preventDefault();
                markAllAsRead();
              }}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              {t("notifications.markAllRead")}
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <ScrollArea className="h-[300px]">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t("notifications.noNotifications")}</p>
            </div>
          ) : (
            notifications.map((notification) => {
              const { Icon, colorClass } = resolveNotificationVisual(notification.type);
              const { title, message } = getLocalizedNotification(notification, t);

              return (
                <DropdownMenuItem
                  key={notification.id}
                  className={cn(
                    "flex items-start gap-3 p-3 cursor-pointer",
                    !notification.is_read && "bg-muted/50",
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className={cn("mt-0.5", colorClass)}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <p
                      className={cn(
                        "text-sm leading-tight",
                        !notification.is_read && "font-medium",
                      )}
                    >
                      {title}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{message}</p>
                    <p className="text-xs text-muted-foreground/70">
                      {formatDistanceToNow(new Date(notification.created_at), {
                        addSuffix: true,
                        locale: dateLocale,
                      })}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <div className="w-2 h-2 bg-primary rounded-full mt-1.5" />
                  )}
                </DropdownMenuItem>
              );
            })
          )}
        </ScrollArea>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="justify-center text-primary cursor-pointer"
          onClick={() => navigate("/notifications")}
        >
          {t("notifications.viewAll")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
